import { useEffect, useRef, useState } from 'react';

import {
  PLAYWRIGHT_HCAPTCHA_TOKEN,
  PRODUCTION_SMOKE_HCAPTCHA_TOKEN,
  isHcaptchaTestMode,
} from './hcaptcha-config';

export { getHcaptchaSiteKey } from './hcaptcha-config';

type HcaptchaWidgetProps = {
  onError: () => void;
  onExpire: () => void;
  onVerify: (token: string) => void;
  productionSmoke?: boolean;
  siteKey: string;
  token: string;
};

type HCaptchaComponent = typeof import('@hcaptcha/react-hcaptcha').default;
type HCaptchaModule = typeof import('@hcaptcha/react-hcaptcha');

export function activateHcaptchaOnIntersection(
  entries: Array<Pick<IntersectionObserverEntry, 'isIntersecting'>>,
  activate: () => void,
) {
  if (!entries.some((entry) => entry.isIntersecting)) {
    return false;
  }

  activate();
  return true;
}

export async function loadHcaptchaComponent(
  loader: () => Promise<HCaptchaModule> = () => import('@hcaptcha/react-hcaptcha'),
) {
  return (await loader()).default;
}

export function HcaptchaWidget({
  onError,
  onExpire,
  onVerify,
  productionSmoke = false,
  siteKey,
  token,
}: HcaptchaWidgetProps) {
  const configuredSiteKey = siteKey.trim();
  const testMode = isHcaptchaTestMode();
  const [HCaptcha, setHCaptcha] = useState<HCaptchaComponent | null>(null);
  const [shouldLoad, setShouldLoad] = useState(productionSmoke || testMode);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (productionSmoke) {
      onVerify(PRODUCTION_SMOKE_HCAPTCHA_TOKEN);
    }
  }, [onVerify, productionSmoke]);

  useEffect(() => {
    if (productionSmoke || testMode) {
      setShouldLoad(true);
      return;
    }

    if (!configuredSiteKey || shouldLoad) {
      return;
    }

    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (activateHcaptchaOnIntersection(entries, () => setShouldLoad(true))) {
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [configuredSiteKey, productionSmoke, shouldLoad, testMode]);

  useEffect(() => {
    if (productionSmoke || !configuredSiteKey || testMode || !shouldLoad) {
      return;
    }

    let active = true;

    loadHcaptchaComponent()
      .then((Component) => {
        if (active) {
          setHCaptcha(() => Component);
        }
      })
      .catch(() => {
        if (active) {
          onError();
        }
      });

    return () => {
      active = false;
    };
  }, [configuredSiteKey, onError, productionSmoke, shouldLoad, testMode]);

  return (
    <div
      className="field"
      data-load-state={shouldLoad ? 'active' : 'deferred'}
      data-production-smoke={productionSmoke ? 'true' : undefined}
      data-sitekey={configuredSiteKey}
      onFocusCapture={() => setShouldLoad(true)}
      onPointerDown={() => setShouldLoad(true)}
      ref={containerRef}
    >
      <label htmlFor="hcaptcha-token">Human verification</label>
      <input id="hcaptcha-token" name="hcaptcha_token" type="hidden" value={token} />
      {configuredSiteKey ? (
        <div className="min-h-[102px] min-w-[328px] rounded-[8px] border border-[var(--line)] bg-white p-3">
          {productionSmoke ? null : testMode ? (
            <button
              className="btn btn-primary"
              onClick={() => onVerify(PLAYWRIGHT_HCAPTCHA_TOKEN)}
              type="button"
            >
              Complete human verification
            </button>
          ) : HCaptcha ? (
            <HCaptcha
              onError={() => onError()}
              onExpire={onExpire}
              onVerify={(nextToken) => onVerify(nextToken)}
              reCaptchaCompat={false}
              sitekey={configuredSiteKey}
            />
          ) : null}
        </div>
      ) : (
        <p className="m-0 rounded-[8px] border border-[var(--line)] bg-white p-3 text-sm font-bold">
          Verification is not configured.
        </p>
      )}
    </div>
  );
}
